import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Royal Lumina" },
      { name: "description", content: "Speak to the Royal Lumina atelier — private orders, gifting, wholesale and press." },
      { property: "og:title", content: "Contact — Royal Lumina" },
      { property: "og:description", content: "Get in touch with the atelier." },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [loading, setLoading] = useState(false);
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      (e.target as HTMLFormElement).reset();
      toast.success("Message sent", { description: "The atelier will reply within one working day." });
    }, 800);
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <AnnouncementBar />
      <Navbar />

      <section className="relative bg-primary text-cream -mt-20 pt-32 pb-16">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_30%,var(--gold)_0%,transparent_45%)]" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-gold/80">Home · Contact</div>
          <h1 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl">Say <span className="italic text-gold">Hello</span></h1>
          <p className="mt-4 max-w-xl text-cream/70">Private orders, gifting, wholesale or press — the atelier replies within one working day.</p>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_1.2fr] gap-12">
        <div className="space-y-4">
          {[
            { icon: MapPin, label: "Atelier", value: "9 Rose Villa, Bandra West, Mumbai 400050" },
            { icon: Mail, label: "Email", value: "concierge@royallumina.com" },
            { icon: Phone, label: "Phone", value: "+91 98765 43210" },
            { icon: Clock, label: "Hours", value: "Mon – Sat · 10:00 to 19:00 IST" },
          ].map((c) => (
            <div key={c.label} className="bg-card border border-border rounded-[20px] p-6 shadow-luxe flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-gold/10 grid place-items-center shrink-0">
                <c.icon className="h-4 w-4 text-gold" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-[0.6rem] tracking-[0.25em] uppercase text-muted-foreground">{c.label}</div>
                <div className="mt-1 font-display text-lg">{c.value}</div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={onSubmit} className="bg-card border border-border rounded-[24px] p-6 sm:p-8 lg:p-10 shadow-luxe">
          <div className="eyebrow">Write to us</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl">A note to <span className="italic text-gold">the atelier</span></h2>

          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <TextField label="Full Name" placeholder="Your name" />
            <TextField label="Email" type="email" placeholder="you@example.com" />
            <div className="md:col-span-2"><TextField label="Subject" placeholder="How can we help?" /></div>
            <div className="md:col-span-2">
              <span className="text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">Message</span>
              <textarea rows={6} placeholder="Tell us a little about the ritual you're planning…" className="mt-2 w-full p-4 bg-background border border-border rounded-[14px] text-sm focus:outline-none focus:border-gold resize-none" />
            </div>
          </div>

          <button type="submit" disabled={loading} className="mt-6 btn-gold h-12 disabled:opacity-70">
            {loading ? "Sending…" : "Send Message"} <Send className="h-4 w-4" />
          </button>
        </form>
      </main>

      <Footer />
    </div>
  );
}

function TextField({ label, type = "text", placeholder }: { label: string; type?: string; placeholder: string }) {
  return (
    <label className="block">
      <span className="text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground">{label}</span>
      <input type={type} placeholder={placeholder} className="mt-2 w-full h-12 px-4 bg-background border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold" />
    </label>
  );
}
